// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.

import './styles/main.css';
import { registerSW } from 'virtual:pwa-register';
import { createUI } from './ui';
import type { UI } from './ui';
import { Tracer, isAbort } from './tracer';
import type { TraceResult } from './types';
import { DEFAULT_PRESET_ID, paramsFromPreset, toPotraceOptions } from './presets';
import { isAcceptedImage } from './imageutil';
import { emit } from './eventlog';

const SAMPLE_URL = `${import.meta.env.BASE_URL}samples/sample-emblem.png`;
const STORE_KEY = 'vectorwell.params.v1';
const MAX_INPUT_BYTES = 40 * 1024 * 1024;

const root = document.getElementById('app')!;
const tracer = new Tracer();

let ui: UI;
let currentFile: File | null = null;
let rasterUrl: string | null = null;
let svgUrl: string | null = null;
let lastResult: TraceResult | null = null;
let debounceTimer = 0;

function main(): void {
  ui = createUI(root, {
    onFile: handleFile,
    onSample: handleSample,
    onChange: scheduleTrace,
    onReset: reset,
    onDownload: download,
    onCopySvg: copySvg,
    onCopyPng: copyPng,
    onShare: share,
  });

  // Restore saved params (fall back to the default preset).
  const saved = loadParams();
  ui.setParams(saved ?? paramsFromPreset(DEFAULT_PRESET_ID));

  ui.showStage('empty');
  ui.setShareVisible(shareSupported());

  emit('system', 'ok', 'Vectorwell ready — tracer will load on first image.');

  registerSW({ immediate: true });
}

// ── File ingestion ─────────────────────────────────────────────────────
function handleFile(file: File): void {
  if (!isAcceptedImage(file)) {
    ui.setError(`"${file.name || 'That file'}" is not a supported image (PNG, JPG, WEBP, GIF, BMP).`);
    emit('input', 'err', 'Rejected unsupported file', { name: file.name, type: file.type });
    return;
  }
  if (file.size > MAX_INPUT_BYTES) {
    ui.setError('That image is over 40 MB — please pick a smaller one.');
    emit('input', 'err', 'Rejected oversized file', { bytes: file.size });
    return;
  }
  ui.clearError();
  currentFile = file;
  if (rasterUrl) URL.revokeObjectURL(rasterUrl);
  rasterUrl = URL.createObjectURL(file);
  ui.setFileName(file.name || 'pasted image');
  ui.showStage('working');
  emit('input', 'ok', 'Image loaded', {
    name: file.name || 'pasted',
    size: `${(file.size / 1024).toFixed(0)} KB`,
  });
  runTrace();
}

async function handleSample(): Promise<void> {
  try {
    emit('input', 'info', 'Loading sample image…');
    const res = await fetch(SAMPLE_URL);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const file = new File([blob], 'sample-emblem.png', { type: 'image/png' });
    handleFile(file);
  } catch (err) {
    ui.setError('Could not load the sample image. Try picking a file instead.');
    emit('input', 'err', 'Sample load failed', { error: String(err) });
  }
}

// ── Tracing ────────────────────────────────────────────────────────────
function scheduleTrace(): void {
  saveParams();
  if (!currentFile) return;
  window.clearTimeout(debounceTimer);
  debounceTimer = window.setTimeout(runTrace, 220);
}

async function runTrace(): Promise<void> {
  if (!currentFile) return;
  const params = ui.getParams();
  const options = toPotraceOptions(params);
  ui.clearError();
  ui.setBusy(true, 'Decoding…', 5);
  emit('trace', 'info', params.color ? 'Tracing (colour)…' : 'Tracing (black & white)…', {
    levels: params.color ? params.levels : 1,
    speckle: params.speckle,
    maxDim: params.maxDim || 'source',
  });

  try {
    const res = await tracer.trace(currentFile, options, params.maxDim, (p) => {
      if (p.phase === 'decode') ui.setBusy(true, 'Decoding…', p.pct);
      else if (p.phase === 'trace') ui.setBusy(true, 'Tracing…'); // indeterminate
      else ui.setBusy(true, 'Finishing…', 100);
    });
    lastResult = res;
    if (svgUrl) URL.revokeObjectURL(svgUrl);
    svgUrl = URL.createObjectURL(new Blob([res.svg], { type: 'image/svg+xml' }));
    ui.showResult(res, svgUrl, rasterUrl!);
    ui.setBusy(false);
    emit('output', 'ok', 'Traced', {
      paths: res.paths,
      colors: res.colors,
      size: `${(res.bytes / 1024).toFixed(1)} KB`,
      ms: res.ms,
    });
  } catch (err) {
    if (isAbort(err)) return; // superseded by a newer trace — ignore
    ui.setBusy(false);
    ui.setError(err instanceof Error ? err.message : 'Tracing failed.');
    emit('trace', 'err', 'Trace failed', { error: String(err) });
  }
}

// ── Output actions ─────────────────────────────────────────────────────
function baseName(): string {
  const n = currentFile?.name || 'vectorwell';
  return n.replace(/\.[^.]+$/, '') || 'vectorwell';
}

async function download(): Promise<void> {
  if (!lastResult) return;
  const blob = new Blob([lastResult.svg], { type: 'image/svg+xml' });
  const name = `${baseName()}.svg`;
  const fsa = (window as unknown as { showSaveFilePicker?: unknown }).showSaveFilePicker;
  if (typeof fsa === 'function') {
    try {
      const handle = await (
        fsa as (o: unknown) => Promise<{ createWritable: () => Promise<WritableStreamDefaultWriter & { write: (d: Blob) => Promise<void>; close: () => Promise<void> }> }>
      )({
        suggestedName: name,
        types: [{ description: 'SVG image', accept: { 'image/svg+xml': ['.svg'] } }],
      });
      const w = await handle.createWritable();
      await w.write(blob);
      await w.close();
      ui.toast('Saved SVG');
      emit('output', 'ok', 'Saved SVG', { name });
      return;
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return; // user cancelled
      // fall through to anchor download
    }
  }
  anchorDownload(blob, name);
  ui.toast('Downloaded SVG');
  emit('output', 'ok', 'Downloaded SVG', { name });
}

function anchorDownload(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

async function copySvg(): Promise<void> {
  if (!lastResult) return;
  try {
    await navigator.clipboard.writeText(lastResult.svg);
    ui.toast('SVG markup copied');
    emit('output', 'ok', 'Copied SVG markup');
  } catch {
    ui.setError('Could not access the clipboard. Try downloading instead.');
  }
}

async function copyPng(): Promise<void> {
  if (!lastResult || !svgUrl) return;
  try {
    const png = await rasterizeSvg(lastResult, svgUrl);
    if (typeof ClipboardItem === 'undefined') throw new Error('no ClipboardItem');
    await navigator.clipboard.write([new ClipboardItem({ 'image/png': png })]);
    ui.toast('PNG preview copied');
    emit('output', 'ok', 'Copied PNG preview');
  } catch {
    ui.setError('Copy as PNG is not available in this browser. Use Download or Copy SVG.');
  }
}

function rasterizeSvg(res: TraceResult, url: string): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const longest = Math.max(res.width, res.height) || 1;
      const scale = Math.min(3, Math.max(1, 1600 / longest));
      const w = Math.max(1, Math.round(res.width * scale));
      const h = Math.max(1, Math.round(res.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('no ctx'));
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/png');
    };
    img.onerror = () => reject(new Error('svg render failed'));
    img.src = url;
  });
}

function shareSupported(): boolean {
  const nav = navigator as Navigator & { canShare?: (d: { files: File[] }) => boolean };
  if (!nav.share || !nav.canShare) return false;
  try {
    const probe = new File(['<svg/>'], 'probe.svg', { type: 'image/svg+xml' });
    return nav.canShare({ files: [probe] });
  } catch {
    return false;
  }
}

async function share(): Promise<void> {
  if (!lastResult) return;
  const file = new File([lastResult.svg], `${baseName()}.svg`, { type: 'image/svg+xml' });
  try {
    await (navigator as Navigator).share({ files: [file], title: 'Vectorwell SVG' });
    emit('output', 'ok', 'Shared SVG');
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') return;
    ui.setError('Sharing failed on this device.');
  }
}

// ── Reset & persistence ────────────────────────────────────────────────
function reset(): void {
  tracer.cancel();
  currentFile = null;
  lastResult = null;
  if (rasterUrl) URL.revokeObjectURL(rasterUrl);
  if (svgUrl) URL.revokeObjectURL(svgUrl);
  rasterUrl = null;
  svgUrl = null;
  ui.clearError();
  ui.setBusy(false);
  ui.showStage('empty');
  emit('ui', 'info', 'Cleared — ready for a new image.');
}

function saveParams(): void {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(ui.getParams()));
  } catch {
    /* storage may be unavailable; non-fatal */
  }
}

function loadParams() {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (typeof p !== 'object' || p === null) return null;
    return {
      color: !!p.color,
      levels: clampInt(p.levels, 2, 16, 5),
      speckle: clampInt(p.speckle, 0, 30, 8),
      corner: clampNum(p.corner, 0, 1.334, 1),
      optimize: clampNum(p.optimize, 0.05, 1, 0.2),
      maxDim: clampInt(p.maxDim, 0, 2000, 1200),
    };
  } catch {
    return null;
  }
}

function clampInt(v: unknown, lo: number, hi: number, dflt: number): number {
  const n = Math.round(Number(v));
  return Number.isFinite(n) ? Math.min(hi, Math.max(lo, n)) : dflt;
}
function clampNum(v: unknown, lo: number, hi: number, dflt: number): number {
  const n = Number(v);
  return Number.isFinite(n) ? Math.min(hi, Math.max(lo, n)) : dflt;
}

// Free object URLs on unload.
window.addEventListener('pagehide', () => {
  if (rasterUrl) URL.revokeObjectURL(rasterUrl);
  if (svgUrl) URL.revokeObjectURL(svgUrl);
});

main();
